import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { UserIcon, EnvelopeIcon, PhoneIcon, BuildingOfficeIcon, TagIcon } from '@heroicons/react/24/outline';

interface AdherentFormData {
  siret: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  description: string;
}

const AdherentCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AdherentFormData>({
    siret: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    description: '',
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AdherentFormData, string>>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special handling for SIRET field to only allow digits
    if (name === 'siret' && !/^\d*$/.test(value)) {
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name as keyof AdherentFormData]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AdherentFormData, string>> = {};
    let isValid = true;

    // SIRET validation (14 digits)
    if (!formData.siret) {
      errors.siret = 'Le SIRET est obligatoire';
      isValid = false;
    } else if (!/^\d{14}$/.test(formData.siret)) {
      errors.siret = 'Le SIRET doit contenir 14 chiffres';
      isValid = false;
    }

    // Email validation
    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      errors.contactEmail = 'Format d\'email invalide';
      isValid = false;
    }

    // Phone validation (simple format check)
    if (formData.contactPhone && !/^[+\d\s()-]{6,20}$/.test(formData.contactPhone)) {
      errors.contactPhone = 'Format de téléphone invalide';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/adherents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      navigate(`/adherents/${response.data.id}`, {
        state: {
          notification: {
            type: 'success',
            message: 'Adhérent créé avec succès',
          },
        },
      });
    } catch (error: any) {
      console.error('Error creating adherent:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la création de l\'adhérent'
      );
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="-mt-4">
        <Link
          to="/adherents"
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à la liste des adhérents
        </Link>
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Ajouter un nouvel adhérent</h1>
          <p className="mt-1 text-sm text-gray-500">
            Remplissez le formulaire pour ajouter un nouvel adhérent.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Erreur: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <div className="px-4 py-5 sm:px-6 bg-blue-50 border-b border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-full p-2 mr-3">
              <UserIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Carte de contact
            </h3>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Informations de l'adhérent
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 gap-x-6">
            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <TagIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="siret" className="block text-sm font-medium text-gray-700">
                    SIRET <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="siret"
                      id="siret"
                      value={formData.siret}
                      onChange={handleChange}
                      maxLength={14}
                      pattern="\d{14}"
                      placeholder="14 chiffres"
                      className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        formErrors.siret ? 'border-red-300 ring-1 ring-red-300' : ''
                      }`}
                      required
                    />
                    {formErrors.siret && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.siret}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <UserIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
                    Nom du contact
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="contactName"
                      id="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Nom et prénom"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <EnvelopeIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                    Email du contact
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      name="contactEmail"
                      id="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        formErrors.contactEmail ? 'border-red-300 ring-1 ring-red-300' : ''
                      }`}
                      placeholder="email@example.com"
                    />
                    {formErrors.contactEmail && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.contactEmail}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <PhoneIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                    Téléphone du contact
                  </label>
                  <div className="mt-1">
                    <input
                      type="tel"
                      name="contactPhone"
                      id="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        formErrors.contactPhone ? 'border-red-300 ring-1 ring-red-300' : ''
                      }`}
                      placeholder="+33 1 23 45 67 89"
                    />
                    {formErrors.contactPhone && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.contactPhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description de l'organisation
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Informations complémentaires sur cet adhérent..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Link
              to="/adherents"
              className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Annuler
            </Link>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                'Créer l\'adhérent'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdherentCreate; 